import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StrongText } from "@/components/strong-text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminUserSummary, useDeleteUser, useMe } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

import { locksAccount, roleLock, roleLockText } from "./utils";

interface Props {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Deletes a user after a confirmation. An account above the signed-in
 * person's role (an owner's for an admin, a developer's for anyone but a
 * developer) cannot be deleted: the dialog says why and offers only Close.
 * The backend refuses it too, as it refuses deleting one's own account.
 */
export function UserDeleteDialog({ user, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const deleteMutation = useDeleteUser();
  const lock = user ? roleLock(me, user) : null;
  const locked = locksAccount(lock);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const handleDelete = async () => {
    if (!user) {
      return;
    }
    setFailure(null);
    try {
      await deleteMutation.mutateAsync({ userId: user.id });
      onOpenChange(false);
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, "deleteUser");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("users.deleteTitle")}</DialogTitle>
          <DialogDescription>
            {locked ? (
              roleLockText(lock)
            ) : (
              <StrongText i18nKey="users.deleteBody" values={{ email: user?.email ?? "" }} />
            )}
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {locked ? t("common.close") : t("common.cancel")}
          </Button>
          {!locked && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("common.delete")
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
